import * as React from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../../lib/utils";

/**
 * Progress bar indicator color variants
 */
const progressIndicatorVariants = cva("h-full rounded-lg transition-all", {
  variants: {
    variant: {
      default: "bg-progress-success",
      critical: "bg-progress-critical", // #a20f2c
      warning: "bg-progress-warning", // #ffa350
      success: "bg-progress-success", // #49b68f
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

export interface ProgressProps
  extends
    React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root>,
    VariantProps<typeof progressIndicatorVariants> {
  /** Custom indicator class name */
  indicatorClassName?: string;
  /** Whether the track should be rectangular (no rounded corners). Default: false (rounded) */
  rectangularTrack?: boolean;
}

/**
 * Progress Component (shadcn/ui style)
 *
 * A progress bar built on @radix-ui/react-progress with color variants.
 *
 * @example
 * ```tsx
 * // Default (success color)
 * <Progress value={75} />
 *
 * // With variant
 * <Progress value={25} variant="critical" />
 * <Progress value={50} variant="warning" />
 * <Progress value={80} variant="success" />
 * ```
 */
const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(
  (
    {
      className,
      value,
      variant,
      indicatorClassName,
      rectangularTrack = false,
      ...props
    },
    ref,
  ) => (
    <ProgressPrimitive.Root
      ref={ref}
      className={cn(
        "relative h-2 w-full bg-progress-track",
        !rectangularTrack && "rounded-lg",
        className,
      )}
      {...props}
    >
      <ProgressPrimitive.Indicator
        className={cn(
          progressIndicatorVariants({ variant }),
          indicatorClassName,
        )}
        style={{ width: `${value || 0}%` }}
      />
    </ProgressPrimitive.Root>
  ),
);
Progress.displayName = ProgressPrimitive.Root.displayName;

export { Progress, progressIndicatorVariants };
